import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Iconify from '../Iconify';
import useAuth from 'src/hooks/useAuth';
import { fToNow } from 'src/utils/formatTime';
import { getListaNotificacoes } from 'src/redux/slices/notificacoes';
import actionLerNotificacao from 'src/actions/notificacoes/actionLerNotificacao';
import { getPrefsAlertas, subscribeAlertas } from 'src/services/notifications';
import { AP } from './AlertasPendentes.design';

// Avisos que chegaram enquanto o usuário estava fora. Os alertas de tarefa só
// existiam em tempo real (socket): quem não estava logado no momento do evento
// via apenas o número no sino, em silêncio. Aqui eles são recuperados no boot.
//
// A preferência é do usuário (Conta → Perfil → Preferências), e o registro de
// "já mostrei" também é por dispositivo — não existe "visto" no servidor.

const CHAVE = 'lais:alertas-mostrados';
const MAX_IDS_GUARDADOS = 200;

// sessao → sessionStorage (o login faz clearAllSessionStorage, então volta a
// aparecer no próximo login, mas não no F5 da mesma aba).
// unica  → localStorage (uma vez por dispositivo, para sempre).
// sempre → sem storage.
function storageDaPolitica(repeticao) {
  try {
    if (repeticao === 'sessao') return window.sessionStorage;
    if (repeticao === 'unica') return window.localStorage;
  } catch {
    // Navegador com storage bloqueado: trata como 'sempre'.
  }
  return null;
}

function lerMostrados(repeticao) {
  const store = storageDaPolitica(repeticao);
  if (!store) return [];
  try {
    const bruto = JSON.parse(store.getItem(CHAVE));
    return Array.isArray(bruto) ? bruto : [];
  } catch {
    return [];
  }
}

// fToNow lança RangeError em data inválida — e o filtro deixa passar avisos sem
// data legível de propósito (melhor mostrar do que engolir). Sem esta proteção
// um único registro com data corrompida derrubaria a pilha inteira no render.
function tempoRelativo(valor) {
  if (!valor) return '';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return fToNow(d);
  } catch {
    return '';
  }
}

function gravarMostrados(repeticao, ids) {
  const store = storageDaPolitica(repeticao);
  if (!store || ids.length === 0) return;
  try {
    const anteriores = lerMostrados(repeticao);
    const juntos = [...anteriores, ...ids.filter(id => !anteriores.includes(id))];
    store.setItem(CHAVE, JSON.stringify(juntos.slice(-MAX_IDS_GUARDADOS)));
  } catch {
    // Sem storage os avisos reaparecem no próximo boot; aceitável.
  }
}

export default function AlertasPendentes() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const naoLidas = useSelector((state) => state.notificacoes?.notificacoesNaoLidas);

  const [pilha, setPilha] = useState([]);
  // Guarda os ids do excedente (não só a contagem) para que "Dispensar" possa
  // marcá-los como vistos — a ação é explícita e deve valer.
  const [restantes, setRestantes] = useState([]);
  const [politica, setPolitica] = useState(getPrefsAlertas);
  // A pilha é montada UMA vez por carga de página: getListaNotificacoes volta a
  // disparar em reconexão de socket, aba escondida >60s e a cada remount de
  // AuthGuard (há dois — /dashboard e /chats). Sem isto os cards ressurgiriam.
  const jaMontou = useRef(false);

  useEffect(() => subscribeAlertas(setPolitica), []);

  useEffect(() => {
    if (jaMontou.current) return;
    if (!user?.id) return;
    if (!politica.ativo) {
      console.debug('[alertas-pendentes] desligado nas preferências do usuário');
      return;
    }
    // A slice não é persistida: lista vazia no primeiro render significa "ainda
    // não carregou", não "nada pendente".
    if (!Array.isArray(naoLidas) || naoLidas.length === 0) return;

    jaMontou.current = true;

    const limite = Date.now() - politica.janelaDias * 24 * 60 * 60 * 1000;
    const mostrados = lerMostrados(politica.repeticao);

    const deTarefa = naoLidas.filter(n => n?.tipo === 'tarefas' && n?._id);
    const naJanela = deTarefa.filter(n => {
      const t = new Date(n?.data_criacao).getTime();
      // Sem data legível o aviso ENTRA: num catch-up é melhor mostrar a mais do
      // que engolir em silêncio, que é justamente o problema que isto resolve.
      return !Number.isFinite(t) || t >= limite;
    });
    const candidatos = naJanela
      // O endpoint devolve sem $sort — ordenar aqui é obrigatório.
      .sort((a, b) => new Date(b?.data_criacao || 0) - new Date(a?.data_criacao || 0))
      .filter(n => !mostrados.includes(String(n._id)));

    const visiveis = candidatos.slice(0, politica.maxVisiveis);
    console.debug(
      `[alertas-pendentes] naoLidas=${naoLidas.length} tarefas=${deTarefa.length} naJanela=${naJanela.length}`
      + ` novos=${candidatos.length} exibindo=${visiveis.length}`,
      { politica, jaMostrados: mostrados.length }
    );

    if (candidatos.length === 0) return;

    setPilha(visiveis);
    setRestantes(candidatos.slice(visiveis.length).map(n => String(n._id)));
    // Marca só os que APARECERAM. Marcar todos os candidatos queimava os
    // excedentes ("+N avisos"), que o usuário nunca viu — eles sumiriam para
    // sempre sem nunca terem sido mostrados.
    gravarMostrados(politica.repeticao, visiveis.map(n => String(n._id)));
  }, [naoLidas, politica, user?.id]);

  // Desligar a preferência com a pilha na tela deve limpá-la na hora.
  useEffect(() => {
    if (!politica.ativo) {
      setPilha([]);
      setRestantes([]);
    }
  }, [politica.ativo]);

  const remover = (id) => setPilha(prev => prev.filter(n => String(n._id) !== String(id)));

  const abrir = (notificacao) => {
    const id = String(notificacao._id);
    remover(id);
    // Navega na hora; marcar como lida é secundário e não deve atrasar o clique.
    const tarefaId = notificacao.tarefa_id?._id || notificacao.tarefa_id;
    navigate(tarefaId ? `/dashboard/tarefas?tarefa=${tarefaId}` : '/dashboard/tarefas');

    actionLerNotificacao(user.id, { notificacaoId: id })
      .then(() => dispatch(getListaNotificacoes(user.id)))
      .catch(err => console.debug('[alertas-pendentes] falha ao marcar como lida', err));
  };

  const dispensarTudo = () => {
    // Dispensa explícita vale também para o excedente que não coube na tela.
    const ids = [...pilha.map(n => String(n._id)), ...restantes];
    gravarMostrados(politica.repeticao, ids);
    setPilha([]);
    setRestantes([]);
  };

  const verTodos = () => {
    dispensarTudo();
    navigate('/dashboard/tarefas');
  };

  // Também renderiza com a pilha vazia enquanto houver excedente: dispensar os
  // cards um a um não pode levar junto o "+N avisos" que ainda não foi tratado.
  if (pilha.length === 0 && restantes.length === 0) return null;

  return (
    <>
      {pilha.map((n) => (
        <div key={String(n._id)} className={AP.card}>
          <div className={AP.iconBox}>
            <Iconify icon="solar:checklist-bold-duotone" width={20} />
          </div>

          <button type="button" onClick={() => abrir(n)} className={`${AP.body} text-left`} title="Abrir tarefa">
            <p className={AP.titulo}>{n.titulo}</p>
            {n.mensagem && <p className={AP.mensagem}>{n.mensagem}</p>}
            {tempoRelativo(n.data_criacao) && <p className={AP.tempo}>há {tempoRelativo(n.data_criacao)}</p>}
          </button>

          <button
            type="button"
            onClick={() => remover(n._id)}
            className={AP.fechar}
            aria-label="Dispensar aviso"
          >
            <Iconify icon="eva:close-outline" width={16} />
          </button>
        </div>
      ))}

      {restantes.length > 0 && (
        <div className={AP.overflow}>
          <button type="button" onClick={verTodos} className={AP.overflowLink}>
            +{restantes.length} {restantes.length === 1 ? 'aviso de tarefa' : 'avisos de tarefas'} — ver todos
          </button>
          <button type="button" onClick={dispensarTudo} className={AP.overflowDispensar}>
            Dispensar
          </button>
        </div>
      )}
    </>
  );
}
